import React, { useState, useEffect, useCallback } from "react";
import { Table, Button, Container, Form, Pagination, Alert, ButtonGroup } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import api from "../api";
import RefreshConnectionButton from '../components/RefreshConnectionButton';

function ShopifyInventory() {
  const [shopifyInventory, setShopifyInventory] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState(null);
  const itemsPerPage = 10;
  const navigate = useNavigate();

  const fetchShopifyInventory = useCallback(async () => {
    if (searchQuery) return;
    const token = localStorage.getItem('token');
    if (!token) {
      setError("Not authenticated. Please log in.");
      navigate('/login');
      return;
    }

    setError(null);
    try {
      const response = await api.get(`/products/shopify?page=${currentPage}&limit=${itemsPerPage}`);
      console.log("📦 Shopify API Response:", response.data);
      setShopifyInventory(response.data.products || []);
      setTotalPages(response.data.totalPages || 1);
    } catch (error) {
      console.error("❌ Error fetching Shopify inventory:", error);
      if (error.response?.status === 401) {
        setError("Session expired. Please log in again.");
        localStorage.removeItem('token');
        setTimeout(() => navigate('/login'), 1500);
      } else {
        setError(error.response?.data?.error || "Failed to load Shopify inventory.");
      }
      setShopifyInventory([]);
    }
  }, [currentPage, searchQuery, navigate]);

  const fetchAllShopifyInventory = useCallback(async () => {
    if (!searchQuery) return;
    const token = localStorage.getItem('token');
    if (!token) {
      setError("Not authenticated. Please log in.");
      navigate('/login');
      return;
    }

    setError(null);
    try {
      let allProducts = [];
      let page = 1;
      let hasMorePages = true;

      while (hasMorePages) {
        const response = await api.get(`/products/shopify?page=${page}&limit=${itemsPerPage}`);
        console.log(`📦 Fetching page ${page}:`, response.data);
        const products = response.data.products || [];
        allProducts = [...allProducts, ...products];

        const totalPagesFromApi = response.data.totalPages || 1;
        hasMorePages = page < totalPagesFromApi;
        page++;
      }

      console.log("📦 All Shopify Products Fetched:", allProducts);
      setShopifyInventory(allProducts);
      setCurrentPage(1);
    } catch (error) {
      console.error("❌ Error fetching all Shopify inventory:", error);
      if (error.response?.status === 401) {
        setError("Session expired. Please log in again.");
        localStorage.removeItem('token');
        setTimeout(() => navigate('/login'), 1500);
      } else {
        setError(error.response?.data?.error || "Failed to fetch all Shopify products.");
      }
      setShopifyInventory([]);
    }
  }, [searchQuery, navigate]);

  useEffect(() => {
    if (searchQuery) {
      fetchAllShopifyInventory();
    } else {
      fetchShopifyInventory();
    }
  }, [fetchShopifyInventory, fetchAllShopifyInventory, searchQuery]);

  const handleEditClick = (sku) => {
    navigate(`/edit/${sku}`);
  };

  const handlePublishToShopify = async () => {
    alert("Publishing to Shopify... (Implement API call here)");
    // TODO: Implement api.post('/list-on-shopify') if needed
  };

  const handleImportFromShopify = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError("Not authenticated. Please log in.");
      navigate('/login');
      return;
    }

    try {
      const response = await api.post("/shopify/import");
 
      alert(response.data.message);
      if (searchQuery) {
        fetchAllShopifyInventory();
      } else {
        fetchShopifyInventory();
      }
    } catch (error) {
      console.error("❌ Error importing from Shopify:", error);
      if (error.response?.status === 401) {
        setError("Session expired. Please log in again.");
        localStorage.removeItem('token');
        setTimeout(() => navigate('/login'), 1500);
      } else {
        alert(error.response?.data?.error || "Failed to import from Shopify.");
      }
    }
  };

  const filteredInventory = Array.isArray(shopifyInventory)
    ? shopifyInventory.filter((product) => {
        // Match products where platform contains "Shopify" (case-insensitive)
        const platformMatch = product.platform && product.platform.toLowerCase().includes('shopify');
        const statusMatch =
          selectedStatus === "All" ||
          (selectedStatus === "Synced" && product.platform && product.platform.toLowerCase().includes('shopify')) ||
          (selectedStatus === "Not Assigned" && !product.platform);
        const searchMatch =
          product.sku?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          product.name?.toLowerCase().includes(searchQuery.toLowerCase());
        return platformMatch && statusMatch && searchMatch;
      })
    : [];

  const paginatedInventory = searchQuery
    ? filteredInventory.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
    : filteredInventory;

  const calculatedTotalPages = searchQuery
    ? Math.ceil(filteredInventory.length / itemsPerPage) || 1
    : totalPages;

  const getImageUrl = (image) => {
    if (!image) return "No Image";
    return image.startsWith("http") ? image : `${window.location.origin}${image}`;
  };

  return (
    <Container className="mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="fw-bold">Shopify Listings - Shopify</h2>
        <div>
          <ButtonGroup>
            <Button variant="success" className="me-2" onClick={handlePublishToShopify}>
              Publish to Shopify
            </Button>
            <Button variant="primary" className="me-2" onClick={handleImportFromShopify}>
              Import from Shopify
            </Button>
            <RefreshConnectionButton platform="Shopify" onRefresh={fetchShopifyInventory} />
          </ButtonGroup>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="d-flex">
        <div className="sidebar" style={{ width: "250px", marginRight: "20px" }}>
          <Form.Group className="mb-3">
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <option value="All">All</option>
              <option value="Synced">Synced</option>
              <option value="Not Assigned">Not Assigned</option>
            </Form.Select>
          </Form.Group>
        </div>

        <div style={{ flex: 1 }}>
          <Form.Group className="mb-3">
            <Form.Control
              type="text"
              placeholder="Search Listings..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </Form.Group>

          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Status</th>
                <th>Image</th>
                <th>Title</th>
                <th>SKU</th>
                <th>Product Type</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedInventory.length > 0 ? (
                paginatedInventory.map((product) => (
                  <tr key={product.sku}>
                    <td>{product.platform && product.platform.toLowerCase().includes('shopify') ? "Synced" : "Not Assigned"}</td>
                    <td>
                      {product.image ? (
                        <img
                          src={getImageUrl(product.image)}
                          alt={product.name || "Product Image"}
                          width="50"
                          height="50"
                          style={{ objectFit: "cover", borderRadius: "5px" }}
                          onError={(e) => (e.target.src = "/default-image-url.jpg")}
                        />
                      ) : (
                        "No Image"
                      )}
                    </td>
                    <td>{product.name || "No Title"}</td>
                    <td>{product.sku || "N/A"}</td>
                    <td>Unknown Type</td>
                    <td>
                      <Button
                        variant="warning"
                        size="sm"
                        className="me-2"
                        onClick={() => handleEditClick(product.sku)}
                      >
                        Edit
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center">
                    No Shopify listings available.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>

          <Pagination className="justify-content-center mt-3">
            <Pagination.First
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            />
            <Pagination.Prev
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            />
            {Array.from({ length: calculatedTotalPages }, (_, i) => i + 1)
              .filter(
                (page) =>
                  page === 1 ||
                  page === calculatedTotalPages ||
                  (page >= currentPage - 2 && page <= currentPage + 2)
              )
              .map((page, index, filteredPages) => (
                <>
                  {index > 0 && page !== filteredPages[index - 1] + 1 && (
                    <Pagination.Ellipsis disabled />
                  )}
                  <Pagination.Item
                    key={page}
                    active={page === currentPage}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </Pagination.Item>
                </>
              ))}
            <Pagination.Next
              onClick={() =>
                setCurrentPage((prev) => Math.min(prev + 1, calculatedTotalPages))
              }
              disabled={currentPage === calculatedTotalPages}
            />
            <Pagination.Last
              onClick={() => setCurrentPage(calculatedTotalPages)}
              disabled={currentPage === calculatedTotalPages}
            />
          </Pagination>
        </div>
      </div>
    </Container>
  );
}

export default ShopifyInventory;